import React from 'react';

export type TokenType = {
  text: string;
  start: number | null;
  end: number | null;
};

export type MatchType = {
  matched: boolean;
  ranges: [number, number][];
};

export type Segment = {
  start: number;
  end: number;
  speaker: number;
  items: TokenType[];
};

export type Caption = {
  id: number;
  start: string;
  end: string;
  tokens: TimedTextToken[];
};

export type SpeakerBlock = {
  speaker: number;
  start: string;
  end: string;
  tokens: TimedTextToken[];
};

export function parseSegments(text: string): Segment[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce<Segment[]>((acc, line) => {
      try {
        const parsed = JSON.parse(line) as Segment;
        acc.push(parsed);
      } catch (error) {
        console.log(error, '-> parseSegments');
      }
      return acc;
    }, []);
}

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

type TimedTextToken = {
  start: number | null;
  end: number | null;
  text: string;
};

function buildText(items: any[]): TimedTextToken[] {
  return items.map((item) => {
    if (Array.isArray(item)) {
      const text = String(item[0] ?? '');
      const start = typeof item[1] === 'number' ? item[1] : null;
      const end = typeof item[2] === 'number' ? item[2] : null;
      return { start, end, text };
    }

    return {
      start: null,
      end: null,
      text: String(item ?? '')
    };
  });
}

export function buildCaptions(segments: Segment[]): Caption[] {
  return segments.map((seg, index) => ({
    id: index + 1,
    start: formatTime(seg.start),
    end: formatTime(seg.end),
    tokens: buildText(seg.items)
  }));
}
export function buildTranscript(segments: Segment[]): SpeakerBlock[] {
  const result: SpeakerBlock[] = [];
  let current: SpeakerBlock | null = null;

  segments.forEach((seg) => {
    const tokens = buildText(seg.items);

    if (!current || current.speaker !== seg.speaker) {
      if (current) result.push(current);

      current = {
        speaker: seg.speaker,
        start: formatTime(seg.start),
        end: formatTime(seg.end),
        tokens
      };
    } else {
      current.tokens.push(...tokens);
      current.end = formatTime(seg.end);
    }
  });

  if (current) result.push(current);

  return result;
}

export function findSearchMatches(
  tokens: TokenType[],
  search: string
): MatchType[] {
  if (!search.trim()) {
    return tokens.map(() => ({ matched: false, ranges: [] }));
  }

  const parts = search.toLowerCase().split(/\s+/).filter(Boolean);
  const isPhrase = parts.length > 1;

  const normalized = tokens.map((t) => t.text.toLowerCase());

  const result: MatchType[] = tokens.map(() => ({
    matched: false,
    ranges: []
  }));

  if (!isPhrase) {
    const word = parts[0];

    normalized.forEach((text, i) => {
      let start = 0;

      while (true) {
        const idx = text.indexOf(word, start);
        if (idx === -1) break;

        result[i].matched = true;
        result[i].ranges.push([idx, idx + word.length]);

        start = idx + word.length;
      }
    });

    return result;
  }

  for (let i = 0; i <= tokens.length - parts.length; i++) {
    let ok = true;

    for (let j = 0; j < parts.length; j++) {
      if (!normalized[i + j].includes(parts[j])) {
        ok = false;
        break;
      }
    }

    if (ok) {
      for (let j = 0; j < parts.length; j++) {
        const idx = normalized[i + j].indexOf(parts[j]);

        if (idx !== -1) {
          result[i + j].matched = true;
          result[i + j].ranges.push([idx, idx + parts[j].length]);
        }
      }
    }
  }

  return result;
}

export function searchHighlight(text: string, ranges: [number, number][]) {
  if (!ranges.length) return text;

  const nodes: React.ReactNode[] = [];
  let last = 0;

  ranges.forEach(([start, end], i) => {
    if (start > last)
      nodes.push(
        <span key={`text-${i}-${last}`}>{text.slice(last, start)}</span>
      );

    nodes.push(
      <span key={i} className="b-token--search">
        <span>{text.slice(start, end)}</span>
      </span>
    );

    last = end;
  });

  if (last < text.length)
    nodes.push(<span key={`text-tail-${last}`}>{text.slice(last)}</span>);

  return nodes;
}
